import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { isId } from "@typedoc/contracts/ids";
import { z } from "zod";
import {
  CONNECTION_TABS,
  ConnectionDetails,
  DetailsSkeleton,
} from "../../../components/connections/connection-details";
import { WebsiteLoginDetails } from "../../../components/connections/website-login-details";
import { NO_SESSION, useConnectionsScope } from "../../../lib/connections/use-connections-scope";
import { membersSwitchSearch } from "../../../lib/connections/members-personal";
import { useMembersPersonal } from "../../../lib/connections/use-members-personal";
import { websiteLoginsQueryOptions } from "../../../lib/queries/connections";

export const Route = createFileRoute("/_authenticated/_workspace/connections/$connectionId")({
  beforeLoad: ({ params }) => {
    if (!isId("connection", params.connectionId)) throw notFound();
  },
  // The tab is in the address, so a reload or a shared link lands where it was, and switching
  // accounts keeps it. A tab this build does not have (a link to the old Ways in) opens the
  // first one instead of an error page.
  validateSearch: z.object({
    tab: z
      .string()
      .optional()
      .transform((value) => CONNECTION_TABS.find((tab) => tab === value)),
    // The list's members-personal switch, carried so an opened account is read the same way.
    members: membersSwitchSearch,
  }),
  component: function ConnectionDetailsRoute() {
    const { connectionId } = Route.useParams();
    const { tab = "permissions" } = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });
    // A website login is a connection of its own (decision 29) with its own pane; the website
    // logins the list already loaded say which IDs are, read the way the list reads them.
    const { context } = useConnectionsScope();
    const members = useMembersPersonal();
    const websites = useQuery({
      ...websiteLoginsQueryOptions(context ?? NO_SESSION, members.options),
      enabled: Boolean(context) && members.ready,
    });
    const login = websites.data?.websiteLogins.find((item) => item.id === connectionId);
    if (context && login) {
      return <WebsiteLoginDetails key={connectionId} login={login} context={context} />;
    }
    if (context && websites.isPending) return <DetailsSkeleton />;
    return (
      <ConnectionDetails
        key={connectionId}
        connectionId={connectionId}
        tab={tab}
        onTabChange={(next) =>
          void navigate({
            search: {
              ...(next === "permissions" ? {} : { tab: next }),
              ...(members.on ? { members: true } : {}),
            },
            replace: true,
            // A tab is a change inside the account, not a new page: the router's page-wide view
            // transition would snapshot the whole pane and replay its entrance, header and tab
            // list included. Only the panel changes, and it fades in on its own (motion.css).
            viewTransition: false,
          })
        }
      />
    );
  },
});
